import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { LanguageContext } from '../App';
import MsMark from './brand/MsMark';
import { lockScroll } from '../utils/scrollLock';
import { TransitionLink } from './PageTransition/PageTransition';
import '../styles/Navbar.css';

/** Rute glavne navigacije. Redosled ovde je redosled u meniju. */
const NAV_ITEMS = [
  { to: '/', key: 'home', end: true },
  { to: '/usluge', key: 'services' },
  { to: '/nas-rad', key: 'portfolio' },
  { to: '/proces', key: 'process' },
  { to: '/cene', key: 'pricing' },
];

export default function Navbar() {
  const { language, toggleLanguage, getTranslation } = useContext(LanguageContext);
  const t = getTranslation('navbar');
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const panelRef = useRef(null);
  const burgerRef = useRef(null);

  // Pozadina navbara postaje neprozirnija cim se krene sa vrha
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Promena rute uvek zatvara mobilni meni
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    burgerRef.current?.focus();
  }, []);

  // Escape zatvara, skrol je zakljucan, fokus ulazi u panel i vraca se na dugme
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') { closeMenu(); return; }
      if (e.key !== 'Tab') return;
      // Zadrzi fokus unutar otvorenog panela
      const focusables = panelRef.current?.querySelectorAll(
        'a[href], button:not([disabled])'
      );
      if (!focusables?.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    const unlockScroll = lockScroll();
    document.addEventListener('keydown', onKey);
    // Sacekaj da panel udje u DOM pa fokusiraj prvu stavku
    const id = window.setTimeout(() => {
      panelRef.current?.querySelector('a[href], button')?.focus();
    }, 60);

    return () => {
      unlockScroll();
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(id);
    };
  }, [menuOpen, closeMenu]);

  const langToggle = (
    <button
      type="button"
      className="lang-toggle"
      onClick={toggleLanguage}
      role="switch"
      aria-checked={language === 'en'}
      aria-label={language === 'sr' ? 'Switch to English' : 'Prebaci na srpski'}
      title="SR / EN"
    >
      <span className="lang-toggle__knob" aria-hidden="true" />
      <span className="lang-toggle__opt" aria-hidden="true">SR</span>
      <span className="lang-toggle__opt" aria-hidden="true">EN</span>
    </button>
  );

  return (
    <header className={`nav${scrolled ? ' is-scrolled' : ''}${menuOpen ? ' is-open' : ''}`}>
      <nav className="nav__inner" aria-label={t.mainNav || 'Glavna navigacija'}>
        <Link to="/" className="nav__brand" aria-label="MS Technology — početna">
          <MsMark className="nav__mark" />
          <span className="nav__wordmark">Technology</span>
        </Link>

        {/* Desktop navigacija */}
        <ul className="nav__links">
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) => `nav__link${isActive ? ' is-active' : ''}`}
              >
                {t[item.key]}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="nav__actions">
          {langToggle}
          <TransitionLink to="/kontakt" kind="grow" className="btn btn--primary btn--sm nav__cta">
            {t.cta}
          </TransitionLink>
        </div>

        {/* Mobilni prekidac */}
        <button
          ref={burgerRef}
          type="button"
          className="nav__burger"
          onClick={() => setMenuOpen((v) => !v)}
          aria-expanded={menuOpen}
          aria-controls="nav-panel"
          aria-label={menuOpen ? (t.closeMenu || 'Zatvori meni') : (t.openMenu || 'Otvori meni')}
        >
          <span className="nav__burger-box" aria-hidden="true">
            <span /><span /><span />
          </span>
        </button>
      </nav>

      {/* Panel i zatamnjenje idu PORTALOM u <body>, ne ostaju u <header>.
          `.nav` ima backdrop-filter, a element sa filterom postaje containing
          block za position:fixed potomke — unutar njega bi se oba pozicionirala
          u odnosu na traku od 62px umesto na ekran (zatamnjenje bi dobilo
          visinu nula, panel pogrešan kontekst slaganja). */}
      {createPortal(
        <>
          <div
            className="nav__scrim"
            onClick={closeMenu}
            aria-hidden="true"
            data-open={menuOpen || undefined}
          />
          <div
            id="nav-panel"
            ref={panelRef}
            className="nav__panel"
            data-open={menuOpen || undefined}
            aria-hidden={!menuOpen}
            inert={!menuOpen}
          >
            <ul className="nav__panel-links">
              {NAV_ITEMS.map((item, i) => (
                <li key={item.to} style={{ '--i': i }}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => `nav__panel-link${isActive ? ' is-active' : ''}`}
                  >
                    {/* aria-hidden: broj je ukras. Bez ovoga čitač ekrana
                        pročita „01 Početna" umesto „Početna". */}
                    <span className="nav__panel-num" aria-hidden="true">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {t[item.key]}
                  </NavLink>
                </li>
              ))}
            </ul>

            <div className="nav__panel-foot" style={{ '--i': NAV_ITEMS.length }}>
              {langToggle}
              <Link to="/kontakt" className="btn btn--primary btn--block">
                {t.cta}
              </Link>
            </div>
          </div>
        </>,
        document.body
      )}
    </header>
  );
}
